import { sql } from "drizzle-orm";
import {
  boolean,
  index,
  jsonb,
  pgTable,
  primaryKey,
  timestamp,
  varchar,
  text,
  integer,
  decimal,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { relations } from "drizzle-orm";

// ─────────────────────────────────────────────────────────────
// Shared enums
// ─────────────────────────────────────────────────────────────
export const userRoleSchema = z.enum(["buyer", "seller", "both"]);
export type UserRole = z.infer<typeof userRoleSchema>;

export const fulfillmentMethodSchema = z.enum([
  "seller_pickup",
  "drop_zone_pickup",
  "local_delivery",
]);

export type FulfillmentMethod = z.infer<typeof fulfillmentMethodSchema>;

export const dayOfWeekSchema = z.enum([
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
]);
export type DayOfWeek = z.infer<typeof dayOfWeekSchema>;

// "HH:MM" 24-hour, e.g. "09:00", "13:30". Stored as wall-clock time
// in the marketplace timezone (America/New_York for now).
export const wallTimeSchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be HH:MM (24-hour)");

export const pickupWindowSchema = z
  .object({
    dayOfWeek: dayOfWeekSchema,
    startTime: wallTimeSchema,
    endTime: wallTimeSchema,
  })
  .refine((w) => w.startTime < w.endTime, {
    message: "startTime must be before endTime",
  });

export type PickupWindow = z.infer<typeof pickupWindowSchema>;

export const movedBySchema = z.enum([
  "buyer",
  "seller",
  "coordinator",
  "courier",
]);

export type MovedBy = z.infer<typeof movedBySchema>;

export const fulfillmentZoneTypeSchema = z.enum([
  "postal_codes",
  "radius",
  "polygon",
]);

export type FulfillmentZoneType = z.infer<typeof fulfillmentZoneTypeSchema>;

// Every value the items.status column actually holds. This enum had drifted:
// published_pending_payments and archived were already in the database while
// the schema still claimed only draft and published.
//
//   draft                      — seller is still writing it
//   published                  — live and purchasable
//   published_pending_payments — live but the seller hasn't finished Stripe, so
//                                buyers get "Notify me" instead of Add to cart
//   archived                   — was live, seller took it down
//   on_request                 — the maker can supply this but isn't holding
//                                stock; buyers register demand instead of buying
//
// items.status is varchar(40) (trap #25), so any new value must fit. The
// longest here is published_pending_payments at 26.
export const itemStatusSchema = z.enum([
  "draft",
  "published",
  "published_pending_payments",
  "archived",
  "on_request",
]);
export type ItemStatus = z.infer<typeof itemStatusSchema>;

// Statuses a buyer can't purchase from but can register interest in. Both the
// buyer-facing request button and the seller's request queue key off this.
export const REQUESTABLE_ITEM_STATUSES: readonly ItemStatus[] = [
  "published_pending_payments",
  "archived",
  "on_request",
];

export function isRequestableStatus(status: string | null | undefined): boolean {
  return (REQUESTABLE_ITEM_STATUSES as readonly string[]).includes(
    String(status || "").trim(),
  );
}

// A listing counts as having a photo only if one of the two photo columns holds
// a non-blank string. Both are nullable and "empty" shows up in prod as NULL,
// "", jsonb null, "[]" and [""] — ListingPhotoManager writes
// `imageUrl: photos[0] || ""`, so blank strings are routine.
//
// Photo presence, not items.status, is the visibility invariant: status is
// stamped once at write time and never re-derived, so a seller emptying their
// photos leaves a `published` row with no image. Every read path gates on this.
export function hasListingPhoto(
  item: { imageUrl?: string | null; imagesJson?: unknown } | null | undefined,
): boolean {
  if (typeof item?.imageUrl === "string" && item.imageUrl.trim() !== "") return true;
  const arr = item?.imagesJson;
  return Array.isArray(arr) && arr.some((u) => typeof u === "string" && u.trim() !== "");
}

// Discretionary notification keys. Operational notifications
// (orders, chat, delivery, cancellations) are not in this map —
// they always fire. Each key here defaults to true; users opt out
// in /account/notifications.
export const notificationPrefsSchema = z.object({
  ripeReady: z.boolean().optional(),
  itemSaved: z.boolean().optional(),
  followActivity: z.boolean().optional(),
  gardenReminders: z.boolean().optional(),
  referrals: z.boolean().optional(),
  deliveryInvites: z.boolean().optional(),
});
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>;

// Whether a fruit/vegetable is currently ripe and ready to ship,
// or still growing on the plant. AI vision sets this on the listing
// at creation time; the seller can override. 'growing' items
// expose a Reserve CTA + estimatedRipeDate to buyers, who pay a
// 25% non-refundable deposit and the 75% balance is auto-charged
// off-session on the ripe date.
export const itemGrowthStateSchema = z.enum(["ripe", "growing"]);
export type ItemGrowthState = z.infer<typeof itemGrowthStateSchema>;

// 48-hour grace window after a buyer creates a reservation.
// Cancellations inside the window = full deposit refund.
// Cancellations after = deposit forfeits and transfers to the seller
// as compensation. Server enforces the cutoff; client surfaces a
// countdown on /reservations and disclaimer copy on the Reserve flow.
export const RESERVATION_BUYER_CANCEL_GRACE_MS = 48 * 60 * 60 * 1000;

// ─────────────────────────────────────────────────────────────
// Session storage table (required for express-session store)
// ─────────────────────────────────────────────────────────────
export const sessions = pgTable(
  "sessions",
  {
    sid: varchar("sid").primaryKey(),
    sess: jsonb("sess").notNull(),
    expire: timestamp("expire").notNull(),
  },
  (table) => [index("IDX_session_expire").on(table.expire)],
);

// ─────────────────────────────────────────────────────────────
// Users
// ─────────────────────────────────────────────────────────────
export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: varchar("email").unique(),
  firstName: varchar("first_name"),
  lastName: varchar("last_name"),
  displayName: varchar("display_name", { length: 120 }),
  // URL-safe slug for a seller's public storefront (/buy/:handle, QR codes).
  // Derived from displayName, unique across users. Null for buyers/unset.
  handle: varchar("handle", { length: 80 }).unique(),
  bio: text("bio"),
  profileImageUrl: varchar("profile_image_url"),
  coverImageUrl: varchar("cover_image_url"),
  sellerType: varchar("seller_type", { length: 30 }),

  addressLine1: varchar("address_line_1", { length: 200 }),
  addressLine2: varchar("address_line_2", { length: 200 }),
  city: varchar("city", { length: 100 }),
  state: varchar("state", { length: 50 }),
  latitude: decimal("latitude", { precision: 10, scale: 6 }),
  longitude: decimal("longitude", { precision: 10, scale: 6 }),
  geocodeSource: varchar("geocode_source", { length: 30 }),
  geocodedAt: timestamp("geocoded_at"),

  roles: varchar("roles", { length: 10 }),
  zipCode: varchar("zip_code", { length: 10 }),
  onboardingCompletedAt: timestamp("onboarding_completed_at"),

  // Email verification — set when the user clicks the link we email at
  // signup (or immediately for OAuth signups, since the provider already
  // proved ownership of the address). Null means unverified; onboarding —
  // and therefore the marketplace — is gated on this being non-null for
  // password-based accounts. See email_verification_tokens below.
  emailVerifiedAt: timestamp("email_verified_at"),

  referralCode: varchar("referral_code", { length: 10 }).unique(),
  referredBy: varchar("referred_by").references((): any => users.id, { onDelete: "set null" }),
  featuredUntil: timestamp("featured_until"),

  passwordHash: varchar("password_hash", { length: 255 }),

  isDemoSeller: integer("is_demo_seller").default(0).notNull(),
  isBetaTester: integer("is_beta_tester").default(0).notNull(),
  isDemoBuyer: integer("is_demo_buyer").default(0).notNull(),

  // Stripe Connect Express — sellers receive payouts through their connected
  // account. stripeAccountId is created when the seller starts onboarding;
  // chargesEnabled / payoutsEnabled are mirrored from Stripe via the
  // account.updated webhook so we can gate listing publish without round-
  // tripping to Stripe on every check.
  stripeAccountId: varchar("stripe_account_id", { length: 64 }),
  stripeChargesEnabled: integer("stripe_charges_enabled").default(0).notNull(),
  stripePayoutsEnabled: integer("stripe_payouts_enabled").default(0).notNull(),
  stripeOnboardingStartedAt: timestamp("stripe_onboarding_started_at"),
  stripeOnboardingCompletedAt: timestamp("stripe_onboarding_completed_at"),
  // Snapshot of `requirements.disabled_reason` from the Stripe account when
  // it's restricted — surfaces in seller dashboard so they know what to fix.
  stripeRestrictedReason: varchar("stripe_restricted_reason", { length: 120 }),

  // Founding Maker program — first 50 sellers who publish a listing get
  // 0% seller fee for 6 months + a permanent badge. isFoundingMaker is set
  // automatically by server logic when the 50th seller publishes their
  // first listing; foundingMakerExpiresAt is set 6 months from that moment.
  isFoundingMaker: integer("is_founding_maker").default(0).notNull(),
  foundingMakerExpiresAt: timestamp("founding_maker_expires_at"),

  // Set by admin once a seller has completed SNAP retailer authorization
  // (in-person, via FNS-252). Gates whether their items can appear in the
  // checkout EBT partition preview — see shared/snap.ts. Bulk-settable with
  // a single UPDATE once a batch of sellers is approved.
  snapApproved: integer("snap_approved").default(0).notNull(),

  // Gluten-free maker. glutenFreeMaker flags a seller as making gluten-free
  // products (set self-serve in account settings or by admin). When set, the
  // listing editor and display honor glutenFreeAllProducts: 1 = "everything I
  // make is gluten-free" (every listing badged, no per-item prompt); 0 = the
  // editor asks "gluten-free?" per product and only answered listings badge.
  glutenFreeMaker: integer("gluten_free_maker").default(0).notNull(),
  glutenFreeAllProducts: integer("gluten_free_all_products").default(0).notNull(),

  // Set when a seller finishes (or dismisses) the one-time retro prompt that
  // asks them to rate the heat of hot-sauce listings created before heat_level
  // existed. Null = still eligible to see the prompt.
  heatPromptDismissedAt: timestamp("heat_prompt_dismissed_at"),

  // Set when a seller finishes (or dismisses) the one-time prompt offering to
  // fold their existing whole-item listings that look like by-weight singles
  // into a weight batch. Null = still eligible to see the prompt.
  weightMigrateDismissedAt: timestamp("weight_migrate_dismissed_at"),

  // Legal acceptance record (Terms of Use + Privacy +, for sellers, Founding
  // Maker Terms roll under one umbrella version in shared/legal.ts). Stores the
  // version the user last affirmatively accepted and when. Null for accounts
  // that predate this record — the client legal-update gate prompts them to
  // accept on next open, which backfills it. Bumping CURRENT_LEGAL_VERSION
  // re-prompts everyone (how mainstream apps roll out updated terms).
  termsAcceptedVersion: varchar("terms_accepted_version", { length: 20 }),
  termsAcceptedAt: timestamp("terms_accepted_at"),

  // Per-seller opt-in: display all listing photos as a portfolio gallery on the
  // storefront. For single-product / made-to-order makers who show one price and
  // many example photos (there's no reliable automatic signal, so it's a flag an
  // admin turns on). Off by default; regular multi-product sellers keep the grid.
  storefrontGalleryEnabled: integer("storefront_gallery_enabled").default(0).notNull(),

  // Unlisted seller: hidden from public browse feeds (items + sellers lists) but
  // the storefront is still reachable by direct link. Used for demo/showcase
  // accounts we want to show privately to prospects without polluting browse for
  // everyone. Off by default.
  unlisted: integer("unlisted").default(0).notNull(),

  // Recurring weekly windows when this seller can have orders
  // ready for driver pickup. Driver picks a 2-hour slot inside one
  // of these windows (within 7 days of the buyer placing the order).
  // Stored nullable so existing code paths that select User columns
  // without this field still type-check; getSellerPickupWindows
  // normalizes null/undefined to an empty array.
  pickupWindows: jsonb("pickup_windows").$type<PickupWindow[]>().default([]),

  // Off-session card capture for reservation balance charges. The buyer's
  // Stripe Customer is created on first reservation; the saved payment
  // method comes from the deposit PaymentIntent (setup_future_usage:
  // 'off_session') so the cron can charge the 75% balance on the ripe
  // date without re-prompting.
  stripeCustomerId: varchar("stripe_customer_id", { length: 64 }),

  // Per-user notification preferences. Operational types (orders, chat,
  // delivery requests, cancellations) ignore this map and always fire.
  // Discretionary types (ripe_ready, item_saved, new_listing,
  // garden_reminder, referral_received) check the matching key — null
  // means "use default", which is true (opt-out, not opt-in).
  notificationPrefs: jsonb("notification_prefs").$type<NotificationPrefs>(),

  reviewsAiSummary: text("reviews_ai_summary"),
  reviewsAiSummaryReviewCount: integer("reviews_ai_summary_review_count"),
  reviewsAiSummaryAt: timestamp("reviews_ai_summary_at"),

  // Set when the user deletes their account. The row is retained but its
  // PII is scrubbed (see POST /api/account/delete); this timestamp both
  // marks the account dead and locks out every existing session via the
  // isAuthenticated middleware.
  deactivatedAt: timestamp("deactivated_at"),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type UpsertUser = typeof users.$inferInsert;
export type User = typeof users.$inferSelect;

export const insertUserSchema = createInsertSchema(users).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  onboardingCompletedAt: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;

// ─────────────────────────────────────────────────────────────
// Drop Zones
// ─────────────────────────────────────────────────────────────
export const dropZones = pgTable("drop_zones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: varchar("name", { length: 200 }).notNull(),
  description: text("description"),

  addressLine1: varchar("address_line_1", { length: 200 }).notNull(),
  addressLine2: varchar("address_line_2", { length: 200 }),
  city: varchar("city", { length: 100 }).notNull(),
  state: varchar("state", { length: 50 }).default("NY").notNull(),
  zipCode: varchar("zip_code", { length: 10 }).notNull(),

  // Coords for plotting the drop zone on the sellers map (satellite pins).
  latitude: decimal("latitude", { precision: 10, scale: 6 }),
  longitude: decimal("longitude", { precision: 10, scale: 6 }),

  contactName: varchar("contact_name", { length: 150 }),
  contactEmail: varchar("contact_email", { length: 200 }),
  contactPhone: varchar("contact_phone", { length: 50 }),

  pickupWindowLabel: varchar("pickup_window_label", { length: 150 }),
  notes: text("notes"),
  isActive: integer("is_active").default(1).notNull(),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertDropZoneSchema = createInsertSchema(dropZones).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDropZone = z.infer<typeof insertDropZoneSchema>;
export type DropZone = typeof dropZones.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Fulfillment Profiles
// ─────────────────────────────────────────────────────────────
export const fulfillmentProfiles = pgTable("fulfillment_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

  sellerId: varchar("seller_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),

  name: varchar("name", { length: 150 }).notNull(),
  description: text("description"),

  fulfillmentMethod: varchar("fulfillment_method", { length: 50 }).notNull(),
  movedBy: varchar("moved_by", { length: 50 }).notNull().default("seller"),

  dropZoneId: varchar("drop_zone_id").references(() => dropZones.id, {
    onDelete: "set null",
  }),

  feeType: varchar("fee_type", { length: 20 }).default("none").notNull(),
  feeFlat: decimal("fee_flat", { precision: 10, scale: 2 }),
  minOrderSubtotal: decimal("min_order_subtotal", { precision: 10, scale: 2 }),

  pickupWindowLabel: varchar("pickup_window_label", { length: 150 }),

  // One-time events (a once-a-year bake sale, a village-wide garage sale). When
  // set to a YYYY-MM-DD date the option is bookable on that date only. Null is
  // the normal recurring-weekday behaviour.
  specificDate: varchar("specific_date", { length: 10 }),

  isActive: integer("is_active").default(1).notNull(),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertFulfillmentProfileSchema = createInsertSchema(
  fulfillmentProfiles,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertFulfillmentProfile = z.infer<
  typeof insertFulfillmentProfileSchema
>;
export type FulfillmentProfile = typeof fulfillmentProfiles.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Fulfillment Zones
// ─────────────────────────────────────────────────────────────
export const fulfillmentZones = pgTable("fulfillment_zones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

  fulfillmentProfileId: varchar("fulfillment_profile_id")
    .notNull()
    .references(() => fulfillmentProfiles.id, { onDelete: "cascade" }),

  zoneType: varchar("zone_type", { length: 30 }).notNull().default("postal_codes"),

  postalCodesJson: jsonb("postal_codes_json"),
  originZipCode: varchar("origin_zip_code", { length: 10 }),
  radiusMiles: integer("radius_miles"),
  polygonGeoJson: jsonb("polygon_geojson"),

  sortOrder: integer("sort_order").default(0).notNull(),
  isActive: integer("is_active").default(1).notNull(),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertFulfillmentZoneSchema = createInsertSchema(
  fulfillmentZones,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertFulfillmentZone = z.infer<typeof insertFulfillmentZoneSchema>;
export type FulfillmentZone = typeof fulfillmentZones.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Items / Products
// ─────────────────────────────────────────────────────────────
// Made-to-order custom fields: the seller defines, per made-to-order listing,
// what the buyer must fill out (flavor, occasion, color, etc.). Definitions
// live on the item; the buyer's answers live on the order line.
export const madeToOrderFieldSchema = z.object({
  id: z.string().min(1).max(40),
  // The customization question shown to the buyer. Sellers like Pink Cookie
  // Girl ask for a lot of detail in one prompt, so this is generous.
  label: z.string().min(1).max(500),
  type: z.enum(["text", "paragraph", "select", "checkbox"]),
  options: z.array(z.string().min(1).max(150)).max(20).optional(),
  required: z.boolean(),
});
export type MadeToOrderField = z.infer<typeof madeToOrderFieldSchema>;
export const madeToOrderFieldsSchema = z.array(madeToOrderFieldSchema).max(15);

export const customResponseSchema = z.object({
  fieldId: z.string(),
  label: z.string(),
  value: z.union([z.string(), z.array(z.string())]),
});
export type CustomResponse = z.infer<typeof customResponseSchema>;

export const items = pgTable("items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  sellerId: varchar("seller_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),

  name: varchar("name", { length: 200 }).notNull(),
  category: varchar("category", { length: 50 }).default("other"),
  subtype: varchar("subtype", { length: 50 }),
  // Canonical hot-sauce heat rating 1-5 (see shared/heat.ts). Nullable: null for
  // non-sauce items and hot sauces the seller hasn't rated yet.
  heatLevel: integer("heat_level"),
  zipCode: varchar("zip_code", { length: 10 }).notNull(),

  price: decimal("price", { precision: 10, scale: 2 }).notNull(),
  groceryAvg: decimal("grocery_avg", { precision: 10, scale: 2 }),

  qtyAvailable: integer("qty_available").default(10).notNull(),
  freshnessNote: varchar("freshness_note", { length: 200 }).default("Picked today"),
  // What the buyer gets for the price (e.g. "per dozen", "each", "per lb").
  // Seller-entered / AI-suggested; shown next to the price so buyers know the
  // amount. Nullable for legacy listings created before this column existed.
  unit: varchar("unit", { length: 60 }),
  shortDescription: text("short_description"),
  attributesJson: jsonb("attributes_json"),
  imageUrl: text("image_url"),
  // Ordered gallery of listing photos (cover = first). Clothing listings need
  // several angles + print detail; legacy items fall back to imageUrl.
  imagesJson: jsonb("images_json").$type<string[]>(),
  // Seller-curated storefront sections: featured pins the item to a shelf on
  // top of the shop; salePrice (when below price) marks it On Sale.
  isFeatured: integer("is_featured").default(0).notNull(),
  salePrice: decimal("sale_price", { precision: 10, scale: 2 }),
  status: varchar("status", { length: 40 }).default("published").notNull(),

  // Reserve-on-the-vine: 'ripe' is the default and behaves exactly like
  // any other listing. 'growing' means the produce is still on the plant
  // — buyers pay a 25% deposit to reserve and the 75% balance auto-
  // charges on estimatedRipeDate. lastGrowthPhotoAt is set whenever the
  // seller posts a fresh weekly progress photo; buyers see the date so
  // they can tell if a seller is keeping the listing honest.
  growthState: varchar("growth_state", { length: 20 }).default("ripe").notNull(),
  estimatedRipeDate: timestamp("estimated_ripe_date"),
  lastGrowthPhotoAt: timestamp("last_growth_photo_at"),

  // Made-to-order: when isMadeToOrder=1 the listing is produced on demand —
  // no fixed stock. leadTimeDays (if set) shows buyers "ready in ~N days";
  // minQty is the smallest order the maker accepts. The buyer's free-text
  // request rides on the order line (orderLines.requestText).
  isMadeToOrder: integer("is_made_to_order").default(0).notNull(),
  leadTimeDays: integer("lead_time_days"),
  minQty: integer("min_qty").default(1).notNull(),

  // Meat share (live-animal / custom-exempt sale): the buyer buys a share of a
  // live animal, not finished meat. It runs through the reservation machinery —
  // the buyer pays the share price in full up front (card saved off-session) and
  // processingFeeCents is auto-charged on slaughterDate (reservation deposit =
  // share price, balance = processing fee, estimatedRipeDate = slaughterDate).
  isMeatShare: integer("is_meat_share").default(0).notNull(),
  processingFeeCents: integer("processing_fee_cents"),
  slaughterDate: timestamp("slaughter_date"),
  // When a made-to-order listing requires a deposit, its orders run through the
  // existing reservation machinery (deposit now, balance auto-charged on the
  // ready date = order date + leadTimeDays).
  requiresDeposit: integer("requires_deposit").default(0).notNull(),
  // Seller-chosen deposit percentage (1-100) for this made-to-order listing.
  // Null falls back to the historical 25%.
  depositPercent: integer("deposit_percent"),
  // Seller-defined custom options the buyer fills out for this made-to-order
  // listing (see madeToOrderFieldSchema). Null/empty = no custom questions.
  customFields: jsonb("custom_fields").$type<MadeToOrderField[]>(),

  // Sell-by-weight (variable weight) batch listing. When soldByWeight is true
  // this item is one size tier of a product group; its inventory is the count
  // of `available` rows in item_weight_units, not qtyAvailable. Each unit has an
  // exact weight + price fixed at listing time (no catch-weight settlement), so
  // checkout stays pay-upfront. size_tier / product_group_id group the tiers;
  // weightMin/Max and unitsAvailable are cached for the card and kept in sync on
  // sale. price_per_lb_cents is the seller's rate used to compute unit prices.
  soldByWeight: boolean("sold_by_weight").default(false).notNull(),
  pricePerLbCents: integer("price_per_lb_cents"),
  sizeTier: varchar("size_tier", { length: 20 }),
  productGroupId: varchar("product_group_id"),
  weightMinCentiLb: integer("weight_min_centi_lb"),
  weightMaxCentiLb: integer("weight_max_centi_lb"),
  unitsAvailable: integer("units_available").default(0).notNull(),

  isActive: integer("is_active").default(1).notNull(),

  // Per-listing gluten-free answer, set when the seller is a gluten-free maker
  // in per-product mode (or stamped to 1 for "all products" makers). null =
  // unanswered / not a gluten-free maker; the badge shows only when this is 1.
  isGlutenFree: integer("is_gluten_free"),

  // Optional per-seller bulk discount, configured on a listing: once the buyer's
  // subtotal WITH THIS SELLER reaches the threshold, take the percent off the
  // seller's items. When a seller has several discounted items in one cart, the
  // most generous percent wins. null = no discount on this listing.
  bulkDiscountThresholdCents: integer("bulk_discount_threshold_cents"),
  bulkDiscountPercent: integer("bulk_discount_percent"),

  // Barter (hidden/allowlisted): when barterEnabled the seller accepts trade
  // offers on this listing. barterAcceptCategories restricts what item
  // categories they'll take (empty/null = open to any); barterNote is a
  // free-text "what I'm looking for" shown to the person proposing a trade.
  barterEnabled: boolean("barter_enabled").default(false).notNull(),
  barterAcceptCategories: jsonb("barter_accept_categories").$type<string[]>(),
  barterNote: text("barter_note"),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertItemSchema = createInsertSchema(items)
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    isActive: true,
  })
  .extend({
    customFields: madeToOrderFieldsSchema.nullish(),
    // estimatedRipeDate arrives as an ISO string over JSON (the Reserve-the-
    // harvest flow sends new Date(...).toISOString()), but createInsertSchema
    // types the timestamp column as z.date(). Coerce so the string is accepted.
    estimatedRipeDate: z.coerce.date().nullish(),
    slaughterDate: z.coerce.date().nullish(),
    processingFeeCents: z.coerce.number().int().min(0).nullish(),
    imagesJson: z.array(z.string()).nullish(),
    salePrice: z.union([z.string(), z.number()]).transform((v) => String(v)).nullish(),
    isFeatured: z.coerce.number().int().min(0).max(1).nullish(),
    isGlutenFree: z.coerce.number().int().min(0).max(1).nullish(),
    heatLevel: z.coerce.number().int().min(0).max(5).nullish(),
    bulkDiscountThresholdCents: z.coerce.number().int().min(0).nullish(),
    bulkDiscountPercent: z.coerce.number().int().min(1).max(90).nullish(),
    barterEnabled: z.coerce.boolean().nullish(),
    barterAcceptCategories: z.array(z.string()).nullish(),
    barterNote: z.string().max(500).nullish(),
  });

export const updateItemSchema = insertItemSchema
  .omit({ sellerId: true })
  .partial();

export type InsertItem = z.infer<typeof insertItemSchema>;
export type UpdateItem = z.infer<typeof updateItemSchema>;
export type Item = typeof items.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Barter (hidden/allowlisted listing-for-listing trades)
// ─────────────────────────────────────────────────────────────
// proposerId offers offeredItemId (qty offeredQty, one of their own listings)
// in exchange for recipientId's targetItemId (qty targetQty). Cashless —
// monetization is a separate barter subscription, not a per-trade fee. status
// walks pending -> accepted | declined | withdrawn; a counter creates a new
// row pointing back via counterOfId.
export const barterOffers = pgTable(
  "barter_offers",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    proposerId: varchar("proposer_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    recipientId: varchar("recipient_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    targetItemId: varchar("target_item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    offeredItemId: varchar("offered_item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    targetQty: integer("target_qty").default(1).notNull(),
    offeredQty: integer("offered_qty").default(1).notNull(),
    status: varchar("status", { length: 20 }).default("pending").notNull(),
    message: text("message"),
    counterOfId: varchar("counter_of_id"),
    createdAt: timestamp("created_at").defaultNow(),
    respondedAt: timestamp("responded_at"),
  },
  (table) => [
    index("IDX_barter_offers_recipient").on(table.recipientId, table.status),
    index("IDX_barter_offers_proposer").on(table.proposerId, table.status),
  ],
);

export const insertBarterOfferSchema = createInsertSchema(barterOffers).omit({
  id: true,
  createdAt: true,
  respondedAt: true,
  status: true,
});
export type InsertBarterOffer = z.infer<typeof insertBarterOfferSchema>;
export type BarterOffer = typeof barterOffers.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Weight units (variable-weight batch listings)
// ─────────────────────────────────────────────────────────────
// One physical unit (e.g. one whole chicken) under a sold-by-weight tier item.
// weight_centi_lb is hundredths of a pound (4.51 lb -> 451) to avoid float drift.
export const itemWeightUnits = pgTable(
  "item_weight_units",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    itemId: varchar("item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    productGroupId: varchar("product_group_id").notNull(),
    weightCentiLb: integer("weight_centi_lb").notNull(),
    // Set for a "price option" unit (half dozen, dozen) where the buyer picks a
    // named size rather than a weighed one. Null on weighed units, which label
    // themselves from weightCentiLb — so the whole sell-by-weight path is
    // untouched. When present, weightCentiLb is 0 and callers must show this.
    label: varchar("label", { length: 60 }),
    priceCents: integer("price_cents").notNull(),
    status: varchar("status", { length: 20 }).default("available").notNull(),
    reservedOrderId: varchar("reserved_order_id"),
    reservedAt: timestamp("reserved_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    index("IDX_item_weight_units_item_status").on(table.itemId, table.status),
    index("IDX_item_weight_units_group").on(table.productGroupId),
  ],
);

export const insertItemWeightUnitSchema = createInsertSchema(itemWeightUnits).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertItemWeightUnit = z.infer<typeof insertItemWeightUnitSchema>;
export type ItemWeightUnit = typeof itemWeightUnits.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Item Fulfillment Profiles
// ─────────────────────────────────────────────────────────────
export const itemFulfillmentProfiles = pgTable("item_fulfillment_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

  itemId: varchar("item_id")
    .notNull()
    .references(() => items.id, { onDelete: "cascade" }),

  fulfillmentProfileId: varchar("fulfillment_profile_id")
    .notNull()
    .references(() => fulfillmentProfiles.id, { onDelete: "cascade" }),

  isDefault: integer("is_default").default(0).notNull(),

  createdAt: timestamp("created_at").defaultNow(),
});

export const insertItemFulfillmentProfileSchema = createInsertSchema(
  itemFulfillmentProfiles,
).omit({
  id: true,
  createdAt: true,
});

export type InsertItemFulfillmentProfile = z.infer<
  typeof insertItemFulfillmentProfileSchema
>;
export type ItemFulfillmentProfile = typeof itemFulfillmentProfiles.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Producer Follows
// ─────────────────────────────────────────────────────────────
export const producerFollows = pgTable(
  "producer_follows",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

    producerId: varchar("producer_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),

    followerId: varchar("follower_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),

    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("IDX_producer_follows_producer").on(table.producerId),
    index("IDX_producer_follows_follower").on(table.followerId),
    uniqueIndex("IDX_producer_follows_pair").on(table.producerId, table.followerId),
  ],
);

export const insertProducerFollowSchema = createInsertSchema(producerFollows).omit({
  id: true,
  createdAt: true,
});

export type InsertProducerFollow = z.infer<typeof insertProducerFollowSchema>;
export type ProducerFollow = typeof producerFollows.$inferSelect;

// Saved Items
// ─────────────────────────────────────────────────────────────
export const savedItems = pgTable(
  "saved_items",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    itemId: varchar("item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("IDX_saved_items_user").on(table.userId),
    index("IDX_saved_items_item").on(table.itemId),
    uniqueIndex("IDX_saved_items_pair").on(table.userId, table.itemId),
  ],
);

export const insertSavedItemSchema = createInsertSchema(savedItems).omit({
  id: true,
  createdAt: true,
});

export type InsertSavedItem = z.infer<typeof insertSavedItemSchema>;
export type SavedItem = typeof savedItems.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Trust & safety: user blocks + content reports (Apple UGC 1.2)
// ─────────────────────────────────────────────────────────────
export const userBlocks = pgTable(
  "user_blocks",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    blockerId: varchar("blocker_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    blockedId: varchar("blocked_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("IDX_user_blocks_blocker").on(table.blockerId),
    uniqueIndex("IDX_user_blocks_pair").on(table.blockerId, table.blockedId),
  ],
);

export type UserBlock = typeof userBlocks.$inferSelect;

// targetType: 'message' | 'review' | 'listing' | 'user'
// reason: short enum-ish string chosen by the reporter (see shared/moderation.ts)
// status: 'open' | 'actioned' | 'dismissed'
export const contentReports = pgTable(
  "content_reports",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    reporterId: varchar("reporter_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    targetType: varchar("target_type", { length: 20 }).notNull(),
    targetId: varchar("target_id").notNull(),
    reason: varchar("reason", { length: 40 }).notNull(),
    details: text("details"),
    status: varchar("status", { length: 20 }).notNull().default("open"),
    createdAt: timestamp("created_at").defaultNow(),
    reviewedAt: timestamp("reviewed_at"),
    reviewedBy: varchar("reviewed_by").references(() => users.id, {
      onDelete: "set null",
    }),
  },
  (table) => [
    index("IDX_content_reports_status").on(table.status),
    index("IDX_content_reports_reporter").on(table.reporterId),
  ],
);

export type ContentReport = typeof contentReports.$inferSelect;

// In-person event signups (QR at a booth): capture an email in the browser to
// claim a free sample, and remember which seller to auto-follow when that email
// later creates a real Stoop account.
export const eventSignups = pgTable(
  "event_signups",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    eventSlug: varchar("event_slug", { length: 60 }).notNull(),
    sellerId: varchar("seller_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    itemId: varchar("item_id").references(() => items.id, { onDelete: "set null" }),
    email: varchar("email", { length: 200 }).notNull(),
    firstName: varchar("first_name", { length: 80 }),
    // 'ios' | 'android' | null — captured so the later app-download email links to
    // the right store (TestFlight for iPhone, Firebase for Android).
    phoneType: varchar("phone_type", { length: 10 }),
    // Email verification: token emailed on claim, verifiedAt set when they click
    // the confirm link. Only verified claims count (real emails) and trigger the
    // "they're verified, give them a cookie" admin/seller alert.
    verifyToken: varchar("verify_token", { length: 64 }),
    verifiedAt: timestamp("verified_at"),
    followApplied: integer("follow_applied").default(0).notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("IDX_event_signups_slug_email").on(table.eventSlug, table.email),
    index("IDX_event_signups_email").on(table.email),
    index("IDX_event_signups_token").on(table.verifyToken),
  ],
);

export type EventSignup = typeof eventSignups.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Relations
// ─────────────────────────────────────────────────────────────
export const dropZonesRelations = relations(dropZones, ({ many }) => ({
  fulfillmentProfiles: many(fulfillmentProfiles),
}));

export const fulfillmentProfilesRelations = relations(
  fulfillmentProfiles,
  ({ one, many }) => ({
    seller: one(users, {
      fields: [fulfillmentProfiles.sellerId],
      references: [users.id],
    }),
    dropZone: one(dropZones, {
      fields: [fulfillmentProfiles.dropZoneId],
      references: [dropZones.id],
    }),
    zones: many(fulfillmentZones),
    timeSlots: many(fulfillmentProfileTimeSlots),
    itemLinks: many(itemFulfillmentProfiles),
  }),
);

export const fulfillmentProfileTimeSlots = pgTable("fulfillment_profile_time_slots", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  fulfillmentProfileId: varchar("fulfillment_profile_id")
    .notNull()
    .references(() => fulfillmentProfiles.id, { onDelete: "cascade" }),
  dayOfWeek: varchar("day_of_week", { length: 20 }).notNull(),
  startTime: varchar("start_time", { length: 20 }).notNull(),
  endTime: varchar("end_time", { length: 20 }).notNull(),
  sortOrder: integer("sort_order").default(0).notNull(),
  isActive: integer("is_active").default(1).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertFulfillmentProfileTimeSlotSchema = createInsertSchema(
  fulfillmentProfileTimeSlots,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertFulfillmentProfileTimeSlot = z.infer<
  typeof insertFulfillmentProfileTimeSlotSchema
>;
export type FulfillmentProfileTimeSlot = typeof fulfillmentProfileTimeSlots.$inferSelect;

export const fulfillmentProfileTimeSlotsRelations = relations(
  fulfillmentProfileTimeSlots,
  ({ one }) => ({
    fulfillmentProfile: one(fulfillmentProfiles, {
      fields: [fulfillmentProfileTimeSlots.fulfillmentProfileId],
      references: [fulfillmentProfiles.id],
    }),
  }),
);

export const fulfillmentZonesRelations = relations(fulfillmentZones, ({ one }) => ({
  fulfillmentProfile: one(fulfillmentProfiles, {
    fields: [fulfillmentZones.fulfillmentProfileId],
    references: [fulfillmentProfiles.id],
  }),
}));

export const itemsRelations = relations(items, ({ one, many }) => ({
  seller: one(users, {
    fields: [items.sellerId],
    references: [users.id],
  }),
  fulfillmentLinks: many(itemFulfillmentProfiles),
}));

export const itemFulfillmentProfilesRelations = relations(
  itemFulfillmentProfiles,
  ({ one }) => ({
    item: one(items, {
      fields: [itemFulfillmentProfiles.itemId],
      references: [items.id],
    }),
    fulfillmentProfile: one(fulfillmentProfiles, {
      fields: [itemFulfillmentProfiles.fulfillmentProfileId],
      references: [fulfillmentProfiles.id],
    }),
  }),
);

// ─────────────────────────────────────────────────────────────
// Order Lines (define before ordersRelations references it)
// ─────────────────────────────────────────────────────────────
export const orderLines = pgTable("order_lines", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id")
    .notNull()
    .references(() => orders.id, { onDelete: "cascade" }),
  itemId: varchar("item_id")
    .notNull()
    .references(() => items.id),

  itemName: varchar("item_name", { length: 200 }).notNull(),
  sellerName: varchar("seller_name", { length: 200 }).notNull(),
  unitPrice: decimal("unit_price", { precision: 10, scale: 2 }).notNull(),
  qty: integer("qty").notNull(),
  // Buyer's free-text request for a made-to-order item (design/flavor/occasion).
  requestText: text("request_text"),
  // Buyer's answers to the listing's seller-defined custom fields.
  customResponses: jsonb("custom_responses").$type<CustomResponse[]>(),
  // Sold-by-weight snapshot: the specific weight unit this line bought and its
  // exact weight, frozen at checkout. weightUnitId is what the webhook marks
  // sold on payment success; weightCentiLb is the display snapshot.
  weightUnitId: varchar("weight_unit_id"),
  weightCentiLb: integer("weight_centi_lb"),
});

// ─────────────────────────────────────────────────────────────
// Orders
// ─────────────────────────────────────────────────────────────
export const orders = pgTable("orders", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  buyerId: varchar("buyer_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),

  subtotal: decimal("subtotal", { precision: 10, scale: 2 }).notNull(),
  platformFee: decimal("platform_fee", { precision: 10, scale: 2 }).notNull(),
  deliveryFee: decimal("delivery_fee", { precision: 10, scale: 2 }),
  total: decimal("total", { precision: 10, scale: 2 }).notNull(),

  deliveryMethod: varchar("delivery_method", { length: 30 }),
  deliveryAddressLine1: varchar("delivery_address_line_1", { length: 200 }),
  deliveryAddressLine2: varchar("delivery_address_line_2", { length: 200 }),
  deliveryCity: varchar("delivery_city", { length: 100 }),
  deliveryState: varchar("delivery_state", { length: 50 }),
  deliveryZipCode: varchar("delivery_zip_code", { length: 10 }),
  deliveryLat: decimal("delivery_lat", { precision: 10, scale: 6 }),
  deliveryLng: decimal("delivery_lng", { precision: 10, scale: 6 }),

  pickupLocation: varchar("pickup_location", { length: 300 }).notNull(),
  pickupWindow: varchar("pickup_window", { length: 150 }).notNull(),

  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 100 }),
  status: varchar("status", { length: 50 }).default("pending").notNull(),

  // Set once payment actually succeeds. Null means the buyer opened checkout
  // but never paid — an abandoned cart, not a real order. Status alone can't
  // tell these apart once such an order is cancelled, and sellers must not see
  // abandoned carts as though a neighbour placed and cancelled an order.
  paidAt: timestamp("paid_at"),

  // How the order was placed. "express" = guest QR/storefront checkout (the
  // buyer may be a passwordless account); null = normal in-app checkout. Used
  // to send the express receipt + account-claim email on payment success.
  source: varchar("source", { length: 20 }),

  // The cart attempt this order came from. A client-generated UUID stamped on
  // every cart_add event and threaded through checkout, so cart -> order
  // conversion is an exact join instead of a fuzzy buyer+timestamp match.
  cartSessionId: varchar("cart_session_id", { length: 40 }),

  // The referral_reward applied to this order, if any. Set at order
  // creation; the reward is marked redeemed (used=1) only after payment
  // confirms via Stripe webhook. SET NULL on reward delete so an
  // orphaned admin cleanup doesn't cascade-delete paid orders.
  appliedRewardId: varchar("applied_reward_id").references(() => referralRewards.id, {
    onDelete: "set null",
  }),

  // Per-order buyer acknowledgment of the food-safety / assumption-of-risk
  // terms (Terms of Use §6). Set only when the cart included a higher-risk
  // category (see FOOD_RISK_CATEGORIES); records the legal version in force at
  // purchase so the exact wording accepted is provable later.
  foodRiskAckVersion: varchar("food_risk_ack_version", { length: 20 }),
  foodRiskAckAt: timestamp("food_risk_ack_at"),

  createdAt: timestamp("created_at").defaultNow(),
});

export const orderFulfillmentSelections = pgTable("order_fulfillment_selections", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id")
    .notNull()
    .references(() => orders.id, { onDelete: "cascade" }),
  sellerId: varchar("seller_id")
    .notNull()
    .references(() => users.id),
  sellerName: varchar("seller_name", { length: 200 }).notNull(),
  fulfillmentProfileId: varchar("fulfillment_profile_id", { length: 100 }),
  fulfillmentMethod: varchar("fulfillment_method", { length: 50 }).notNull(),
  buyerLabel: varchar("buyer_label", { length: 100 }).notNull(),
  dropZoneName: varchar("drop_zone_name", { length: 200 }),
  dropZoneCity: varchar("drop_zone_city", { length: 120 }),
  dropZoneState: varchar("drop_zone_state", { length: 50 }),
  scheduledDate: varchar("scheduled_date", { length: 20 }).notNull(),
  timeWindowLabel: varchar("time_window_label", { length: 120 }).notNull(),
  dayLabel: varchar("day_label", { length: 120 }),
  status: varchar("status", { length: 50 }).default("pending").notNull(),
  // Three-step handshake between seller and driver. The first two are
  // seller-set timestamps; the third lives on delivery_requests.
  scheduleConfirmedAt: timestamp("schedule_confirmed_at"),
  readyConfirmedAt: timestamp("ready_confirmed_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const ordersRelations = relations(orders, ({ one, many }) => ({
  buyer: one(users, {
    fields: [orders.buyerId],
    references: [users.id],
  }),
  orderLines: many(orderLines),
  fulfillmentSelections: many(orderFulfillmentSelections),
}));

export const insertOrderSchema = createInsertSchema(orders).omit({
  id: true,
  createdAt: true,
});

export type InsertOrder = z.infer<typeof insertOrderSchema>;
export type Order = typeof orders.$inferSelect;

export const insertOrderFulfillmentSelectionSchema = createInsertSchema(
  orderFulfillmentSelections,
).omit({
  id: true,
  createdAt: true,
});

export type InsertOrderFulfillmentSelection = z.infer<
  typeof insertOrderFulfillmentSelectionSchema
>;
export type OrderFulfillmentSelection = typeof orderFulfillmentSelections.$inferSelect;

// Now that orders is defined, we can define orderLinesRelations safely
export const orderFulfillmentSelectionsRelations = relations(
  orderFulfillmentSelections,
  ({ one }) => ({
    order: one(orders, {
      fields: [orderFulfillmentSelections.orderId],
      references: [orders.id],
    }),
    seller: one(users, {
      fields: [orderFulfillmentSelections.sellerId],
      references: [users.id],
    }),
  }),
);

// ─────────────────────────────────────────────────────────────
// Notifications
// ─────────────────────────────────────────────────────────────
export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  type: varchar("type", { length: 50 }).notNull(),
  title: varchar("title", { length: 200 }).notNull(),
  body: text("body").notNull(),
  dataJson: jsonb("data_json"),
  isRead: integer("is_read").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertNotificationSchema = createInsertSchema(notifications).omit({
  id: true,
  createdAt: true,
});

export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Device Tokens (for native push via FCM)
// ─────────────────────────────────────────────────────────────
export const deviceTokens = pgTable("device_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  platform: varchar("platform", { length: 20 }).notNull(),
  token: text("token").notNull().unique(),
  isActive: integer("is_active").default(1).notNull(),
  lastSeenAt: timestamp("last_seen_at").defaultNow(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDeviceTokenSchema = createInsertSchema(deviceTokens).omit({
  id: true,
  createdAt: true,
  lastSeenAt: true,
});

export type InsertDeviceToken = z.infer<typeof insertDeviceTokenSchema>;
export type DeviceToken = typeof deviceTokens.$inferSelect;

// ─────────────────────────────────────────────────────────────
// OAuth Accounts (third-party identity providers — Google, Apple, etc.)
// ─────────────────────────────────────────────────────────────
export const oauthAccounts = pgTable(
  "oauth_accounts",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    provider: varchar("provider", { length: 30 }).notNull(),
    providerUserId: varchar("provider_user_id", { length: 200 }).notNull(),
    email: varchar("email", { length: 255 }),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("oauth_accounts_provider_user_id_unique").on(
      table.provider,
      table.providerUserId
    ),
  ]
);

export const insertOAuthAccountSchema = createInsertSchema(oauthAccounts).omit({
  id: true,
  createdAt: true,
});

export type InsertOAuthAccount = z.infer<typeof insertOAuthAccountSchema>;
export type OAuthAccount = typeof oauthAccounts.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Beta feedback (demo-mode only — table auto-hides when
// DEMO_ORDER_ROUTING_ENABLED flips to false)
// ─────────────────────────────────────────────────────────────
export const feedback = pgTable("feedback", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  email: varchar("email", { length: 255 }),
  category: varchar("category", { length: 30 }).default("other").notNull(),
  message: text("message").notNull(),
  pageUrl: text("page_url"),
  userAgent: text("user_agent"),
  appVersion: varchar("app_version", { length: 40 }),
  diagnostics: jsonb("diagnostics"),
  status: varchar("status", { length: 20 }).default("new").notNull(),
  adminNotes: text("admin_notes"),
  // Heuristic topic tag (Cart/Checkout, AI/Quick Add, Delivery/Driver…)
  // Computed from message on insert via themeForMessage. Stored so
  // admin queries can filter/group server-side without re-running the
  // regex per request, and so it's indexable when the queue grows.
  theme: varchar("theme", { length: 60 }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertFeedbackSchema = createInsertSchema(feedback).omit({
  id: true,
  createdAt: true,
  status: true,
  adminNotes: true,
  theme: true,
});

export type InsertFeedback = z.infer<typeof insertFeedbackSchema>;
export type Feedback = typeof feedback.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Feedback triage log — audit trail for the Claude triage workflow.
// Every dispatch (successful or not) gets a row so you can reconcile
// server events with GitHub Actions runs and PRs.
// ─────────────────────────────────────────────────────────────
export const feedbackTriageLog = pgTable("feedback_triage_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  feedbackId: varchar("feedback_id")
    .notNull()
    .references(() => feedback.id, { onDelete: "cascade" }),
  status: varchar("status", { length: 30 }).notNull(),
  workflowRunUrl: text("workflow_run_url"),
  prUrl: text("pr_url"),
  notes: text("notes"),
  createdAt: timestamp("created_at").defaultNow(),
});

export type FeedbackTriageLog = typeof feedbackTriageLog.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Triage runs — execution log + live narration for the local Mac
// triage daemon (and any other no-GitHub-Actions executor). One row
// per attempt to triage a feedback. The daemon long-polls /api/triage/next
// for queued rows, claims one, runs Claude Code, and streams narration
// chunks back via /api/triage/runs/:id/event. The admin UI reads this
// table to render GitHub-Actions-style live logs.
// ─────────────────────────────────────────────────────────────
export const triageRuns = pgTable("triage_runs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  feedbackId: varchar("feedback_id")
    .notNull()
    .references(() => feedback.id, { onDelete: "cascade" }),
  // queued → claimed → running → success | failed | cancelled
  status: varchar("status", { length: 20 }).default("queued").notNull(),
  source: varchar("source", { length: 30 }).default("mac-daemon").notNull(),
  claimedBy: varchar("claimed_by", { length: 80 }),
  // Append-only event log; each entry { at, kind, text } where kind ∈
  // step | tool | commit | error | info. The daemon batches POSTs.
  narration: jsonb("narration").default(sql`'[]'::jsonb`).notNull(),
  commitSha: varchar("commit_sha", { length: 40 }),
  summary: text("summary"),
  errorMessage: text("error_message"),
  queuedAt: timestamp("queued_at").defaultNow(),
  claimedAt: timestamp("claimed_at"),
  startedAt: timestamp("started_at"),
  endedAt: timestamp("ended_at"),
});

export type TriageRun = typeof triageRuns.$inferSelect;
export type InsertTriageRun = typeof triageRuns.$inferInsert;

// ─────────────────────────────────────────────────────────────
// Referral rewards (10% mutual discount on referral signup)
// ─────────────────────────────────────────────────────────────
export const referralRewards = pgTable(
  "referral_rewards",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    type: varchar("type", { length: 20 }).notNull(),
    discountPercent: integer("discount_percent").notNull().default(10),
    label: varchar("label", { length: 200 }).notNull(),
    referralUserId: varchar("referral_user_id").references(() => users.id, { onDelete: "set null" }),
    used: integer("used").default(0).notNull(),
    usedAt: timestamp("used_at"),
    orderId: varchar("order_id"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("idx_referral_rewards_user_used").on(table.userId, table.used),
    index("idx_referral_rewards_type_discount").on(table.type, table.discountPercent),
  ],
);

export type ReferralReward = typeof referralRewards.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Messages (buyer–seller chat per order)
// ─────────────────────────────────────────────────────────────
export const messages = pgTable("messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id")
    .notNull()
    .references(() => orders.id, { onDelete: "cascade" }),
  senderId: varchar("sender_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  recipientId: varchar("recipient_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  body: text("body").notNull(),
  // Optional image attachment URL. Only allowed on made-to-order order chats
  // (mockups / inspiration); enforced server-side. Body may be "" for an
  // image-only message.
  imageUrl: varchar("image_url", { length: 600 }),
  isRead: integer("is_read").default(0).notNull(),
  // Per-user emoji reactions: { [userId]: emoji }. One reaction per user,
  // replaceable. Emoji must be one of MESSAGE_REACTION_EMOJIS.
  reactions: jsonb("reactions").$type<Record<string, string>>(),
  createdAt: timestamp("created_at").defaultNow(),
});

// Allowed message-reaction emoji, shared by the client picker and the server
// validation so the set stays in one place. 🚪 is the Stoop reaction.
export const MESSAGE_REACTION_EMOJIS = [
  "❤️",
  "😂",
  "👍",
  "😮",
  "😢",
  "🙏",
  "🚪",
] as const;

export const insertMessageSchema = createInsertSchema(messages).omit({
  id: true,
  createdAt: true,
  isRead: true,
});

export type InsertMessage = z.infer<typeof insertMessageSchema>;
export type Message = typeof messages.$inferSelect;

export const messagesRelations = relations(messages, ({ one }) => ({
  order: one(orders, {
    fields: [messages.orderId],
    references: [orders.id],
  }),
  sender: one(users, {
    fields: [messages.senderId],
    references: [users.id],
    relationName: "messageSender",
  }),
  recipient: one(users, {
    fields: [messages.recipientId],
    references: [users.id],
    relationName: "messageRecipient",
  }),
}));

// ─────────────────────────────────────────────────────────────
// Buyer saved addresses
// ─────────────────────────────────────────────────────────────
export const buyerAddresses = pgTable("buyer_addresses", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  label: varchar("label", { length: 50 }).notNull(),
  addressLine1: varchar("address_line_1", { length: 200 }).notNull(),
  addressLine2: varchar("address_line_2", { length: 200 }),
  city: varchar("city", { length: 100 }).notNull(),
  state: varchar("state", { length: 50 }).notNull(),
  zipCode: varchar("zip_code", { length: 10 }).notNull(),
  latitude: decimal("latitude", { precision: 10, scale: 6 }),
  longitude: decimal("longitude", { precision: 10, scale: 6 }),
  isDefault: integer("is_default").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertBuyerAddressSchema = createInsertSchema(buyerAddresses).omit({
  id: true,
  createdAt: true,
});

export type InsertBuyerAddress = z.infer<typeof insertBuyerAddressSchema>;
export type BuyerAddress = typeof buyerAddresses.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Driver profiles (opt-in for community delivery)
// ─────────────────────────────────────────────────────────────
export const driverProfiles = pgTable("driver_profiles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .unique()
    .references(() => users.id, { onDelete: "cascade" }),
  vehicleType: varchar("vehicle_type", { length: 20 }).notNull().default("car"),
  vehicleMake: varchar("vehicle_make", { length: 50 }),
  vehicleModel: varchar("vehicle_model", { length: 50 }),
  vehicleYear: varchar("vehicle_year", { length: 4 }),
  vehicleColor: varchar("vehicle_color", { length: 30 }),
  licensePlate: varchar("license_plate", { length: 20 }),
  driversLicenseState: varchar("drivers_license_state", { length: 2 }),
  driversLicenseExpiry: varchar("drivers_license_expiry", { length: 10 }),
  hasInsurance: integer("has_insurance").default(0).notNull(),
  dateOfBirth: varchar("date_of_birth", { length: 10 }),
  maxMiles: integer("max_miles").default(25).notNull(),
  isActive: integer("is_active").default(1).notNull(),
  rating: decimal("rating", { precision: 3, scale: 2 }),
  totalDeliveries: integer("total_deliveries").default(0).notNull(),
  totalEarnings: decimal("total_earnings", { precision: 10, scale: 2 }).default("0.00").notNull(),
  isDemoDriver: integer("is_demo_driver").default(0).notNull(),
  homeLat: decimal("home_lat", { precision: 10, scale: 6 }),
  homeLng: decimal("home_lng", { precision: 10, scale: 6 }),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDriverProfileSchema = createInsertSchema(driverProfiles).omit({
  id: true,
  createdAt: true,
  rating: true,
  totalDeliveries: true,
  totalEarnings: true,
});

export type InsertDriverProfile = z.infer<typeof insertDriverProfileSchema>;
export type DriverProfile = typeof driverProfiles.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Delivery requests (community delivery bulletin board)
// ─────────────────────────────────────────────────────────────
export const deliveryRequests = pgTable("delivery_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id")
    .notNull()
    .references(() => orders.id, { onDelete: "cascade" }),
  requestedByUserId: varchar("requested_by_user_id")
    .notNull()
    .references(() => users.id),
  status: varchar("status", { length: 20 }).default("open").notNull(),

  buyerAddressLine1: varchar("buyer_address_line_1", { length: 200 }).notNull(),
  buyerAddressLine2: varchar("buyer_address_line_2", { length: 200 }),
  buyerCity: varchar("buyer_city", { length: 100 }).notNull(),
  buyerState: varchar("buyer_state", { length: 50 }).notNull(),
  buyerZipCode: varchar("buyer_zip_code", { length: 10 }).notNull(),
  buyerLat: decimal("buyer_lat", { precision: 10, scale: 6 }),
  buyerLng: decimal("buyer_lng", { precision: 10, scale: 6 }),

  estimatedMiles: decimal("estimated_miles", { precision: 6, scale: 1 }),
  estimatedStops: integer("estimated_stops").notNull(),
  driverPay: decimal("driver_pay", { precision: 10, scale: 2 }).notNull(),
  deliveryFee: decimal("delivery_fee", { precision: 10, scale: 2 }).notNull(),
  platformCut: decimal("platform_cut", { precision: 10, scale: 2 }).notNull(),

  acceptedByUserId: varchar("accepted_by_user_id").references(() => users.id),
  acceptedAt: timestamp("accepted_at"),
  scheduledFor: timestamp("scheduled_for"),
  scheduledAt: timestamp("scheduled_at"),
  // When driver tapped "Got it, on my way" after a seller marked the
  // order ready. Closes the third leg of the seller↔driver handshake.
  pickupAcknowledgedAt: timestamp("pickup_acknowledged_at"),
  // Earliest delivery date the buyer chose (>= the seller's made-to-order
  // ready date). The driver delivers between this date and deliverByDate.
  requestedDeliveryDate: timestamp("requested_delivery_date"),
  deliverByDate: timestamp("deliver_by_date").notNull(),
  // Founder free-delivery orders: the buyer paid $0, so this is an UNPAID
  // volunteer delivery. It still goes to the whole pool, but the driver UI
  // shows it's not paid and the app creator backstops it if no one volunteers.
  isFounderDelivery: boolean("is_founder_delivery").default(false).notNull(),
  completedAt: timestamp("completed_at"),
  cancelledAt: timestamp("cancelled_at"),
  cancelReason: varchar("cancel_reason", { length: 300 }),

  simulationSpeed: decimal("simulation_speed", { precision: 4, scale: 1 }).default("1.0").notNull(),

  createdAt: timestamp("created_at").defaultNow(),
});

export const insertDeliveryRequestSchema = createInsertSchema(deliveryRequests).omit({
  id: true,
  createdAt: true,
  acceptedByUserId: true,
  acceptedAt: true,
  scheduledFor: true,
  scheduledAt: true,
  completedAt: true,
  cancelledAt: true,
  cancelReason: true,
  simulationSpeed: true,
});

export type InsertDeliveryRequest = z.infer<typeof insertDeliveryRequestSchema>;
export type DeliveryRequest = typeof deliveryRequests.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Delivery request stops (seller pickup points for multi-seller)
// ─────────────────────────────────────────────────────────────
export const deliveryRequestStops = pgTable("delivery_request_stops", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryRequestId: varchar("delivery_request_id")
    .notNull()
    .references(() => deliveryRequests.id, { onDelete: "cascade" }),
  sellerId: varchar("seller_id")
    .notNull()
    .references(() => users.id),
  sellerName: varchar("seller_name", { length: 200 }).notNull(),
  addressLine1: varchar("address_line_1", { length: 200 }).notNull(),
  addressLine2: varchar("address_line_2", { length: 200 }),
  city: varchar("city", { length: 100 }).notNull(),
  state: varchar("state", { length: 50 }).notNull(),
  zipCode: varchar("zip_code", { length: 10 }).notNull(),
  lat: decimal("lat", { precision: 10, scale: 6 }),
  lng: decimal("lng", { precision: 10, scale: 6 }),
  sortOrder: integer("sort_order").default(0).notNull(),
  status: varchar("status", { length: 20 }).default("pending").notNull(),
  pickedUpAt: timestamp("picked_up_at"),
  pickupPhotoUrl: varchar("pickup_photo_url", { length: 500 }),
  pickupProofLat: decimal("pickup_proof_lat", { precision: 10, scale: 6 }),
  pickupProofLng: decimal("pickup_proof_lng", { precision: 10, scale: 6 }),
  pickupProofDistanceMeters: integer("pickup_proof_distance_meters"),
  offlineCaptured: boolean("offline_captured").default(false).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
});

export type DeliveryRequestStop = typeof deliveryRequestStops.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Delivery confirmations (proof of delivery)
// ─────────────────────────────────────────────────────────────
export const deliveryConfirmations = pgTable("delivery_confirmations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryRequestId: varchar("delivery_request_id")
    .notNull()
    .references(() => deliveryRequests.id, { onDelete: "cascade" }),
  driverUserId: varchar("driver_user_id")
    .notNull()
    .references(() => users.id),
  photoUrl: varchar("photo_url", { length: 500 }),
  notes: text("notes"),
  proofLat: decimal("proof_lat", { precision: 10, scale: 6 }),
  proofLng: decimal("proof_lng", { precision: 10, scale: 6 }),
  proofDistanceMeters: integer("proof_distance_meters"),
  offlineCaptured: boolean("offline_captured").default(false).notNull(),
  confirmedAt: timestamp("confirmed_at").defaultNow(),
});

export type DeliveryConfirmation = typeof deliveryConfirmations.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Driver live location (for real-time buyer tracking)
// ─────────────────────────────────────────────────────────────
export const driverLocations = pgTable("driver_locations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  deliveryRequestId: varchar("delivery_request_id")
    .notNull()
    .references(() => deliveryRequests.id, { onDelete: "cascade" }),
  driverUserId: varchar("driver_user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  latitude: decimal("latitude", { precision: 10, scale: 6 }).notNull(),
  longitude: decimal("longitude", { precision: 10, scale: 6 }).notNull(),
  heading: decimal("heading", { precision: 5, scale: 1 }),
  speed: decimal("speed", { precision: 5, scale: 1 }),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export type DriverLocation = typeof driverLocations.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Reviews (post-transaction, one per order)
// ─────────────────────────────────────────────────────────────
export const reviews = pgTable("reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id")
    .notNull()
    .unique()
    .references(() => orders.id, { onDelete: "cascade" }),
  buyerId: varchar("buyer_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  sellerId: varchar("seller_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  rating: integer("rating").notNull(),
  title: varchar("title", { length: 200 }),
  body: text("body"),
  sellerResponse: text("seller_response"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertReviewSchema = createInsertSchema(reviews).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  sellerResponse: true,
});

export type InsertReview = z.infer<typeof insertReviewSchema>;
export type Review = typeof reviews.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Driver reviews (post-delivery, one per order, separate from
// seller reviews so the two can coexist on the same order)
// ─────────────────────────────────────────────────────────────
export const driverReviews = pgTable("driver_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  orderId: varchar("order_id")
    .notNull()
    .unique()
    .references(() => orders.id, { onDelete: "cascade" }),
  deliveryRequestId: varchar("delivery_request_id")
    .notNull()
    .references(() => deliveryRequests.id, { onDelete: "cascade" }),
  buyerId: varchar("buyer_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  driverId: varchar("driver_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  rating: integer("rating").notNull(),
  title: varchar("title", { length: 200 }),
  body: text("body"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertDriverReviewSchema = createInsertSchema(driverReviews).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertDriverReview = z.infer<typeof insertDriverReviewSchema>;
export type DriverReview = typeof driverReviews.$inferSelect;

export const orderLinesRelations = relations(orderLines, ({ one }) => ({
  order: one(orders, {
    fields: [orderLines.orderId],
    references: [orders.id],
  }),
  item: one(items, {
    fields: [orderLines.itemId],
    references: [items.id],
  }),
}));

export const insertOrderLineSchema = createInsertSchema(orderLines).omit({
  id: true,
});

export type InsertOrderLine = z.infer<typeof insertOrderLineSchema>;
export type OrderLine = typeof orderLines.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Cart items (session-based)
// ─────────────────────────────────────────────────────────────
export interface CartItem {
  itemId: string;
  qty: number;
  // Buyer's free-text request for a made-to-order item; sent to the server at
  // checkout and stored on the order line.
  requestText?: string;
  // Buyer's answers to the listing's seller-defined custom fields.
  customResponses?: CustomResponse[];
  // For a sold-by-weight tier line: the specific reserved physical unit and its
  // exact weight/price, chosen via the size picker. Threaded to the server at
  // checkout so the order line snapshots the exact bird.
  weightUnitId?: string;
  unitWeightCentiLb?: number;
  unitPriceCents?: number;
}

export interface CartItemWithDetails extends CartItem {
  item: Item;
  lineSubtotal: number;
}

// ─────────────────────────────────────────────────────────────
// Local Recipes (AI-generated, cached per zip + day)
// ─────────────────────────────────────────────────────────────
export const localRecipes = pgTable(
  "local_recipes",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    zipCode: varchar("zip_code", { length: 10 }).notNull(),
    dateBucket: varchar("date_bucket", { length: 10 }).notNull(),
    ingredientsHash: varchar("ingredients_hash", { length: 64 }).notNull(),
    recipes: jsonb("recipes").notNull(),
    generatedAt: timestamp("generated_at").defaultNow(),
  },
  (table) => [
    uniqueIndex("local_recipes_zip_date_idx").on(table.zipCode, table.dateBucket),
  ],
);

export type LocalRecipeRow = typeof localRecipes.$inferSelect;

export interface LocalRecipeIngredient {
  name: string;
  quantityLabel: string;
  quantityNeeded?: number;
  optional: boolean;
  pantryStaple: boolean;
}

export interface LocalRecipe {
  id: string;
  title: string;
  description: string;
  servingsDefault: number;
  prepMinutes: number;
  dietaryTags: string[];
  ingredients: LocalRecipeIngredient[];
  steps: string[];
}

// ─────────────────────────────────────────────────────────────
// Password Reset Tokens
// ─────────────────────────────────────────────────────────────
export const passwordResetTokens = pgTable("password_reset_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  tokenHash: varchar("token_hash", { length: 128 }).notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertPasswordResetTokenSchema = createInsertSchema(passwordResetTokens).omit({
  id: true,
  createdAt: true,
  usedAt: true,
});

export type InsertPasswordResetToken = z.infer<typeof insertPasswordResetTokenSchema>;
export type PasswordResetToken = typeof passwordResetTokens.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Email Verification Tokens
// ─────────────────────────────────────────────────────────────
// Mirrors passwordResetTokens: a single-use, hashed, expiring token
// emailed at signup. Clicking the link marks users.emailVerifiedAt. We
// store only the SHA-256 hash of the raw token, never the token itself.
export const emailVerificationTokens = pgTable("email_verification_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  tokenHash: varchar("token_hash", { length: 128 }).notNull().unique(),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertEmailVerificationTokenSchema = createInsertSchema(emailVerificationTokens).omit({
  id: true,
  createdAt: true,
  usedAt: true,
});

export type InsertEmailVerificationToken = z.infer<typeof insertEmailVerificationTokenSchema>;
export type EmailVerificationToken = typeof emailVerificationTokens.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Pending Referrals (deferred-deep-link attribution)
// ─────────────────────────────────────────────────────────────
// Filled by /install?ref=CODE so a referee who clicks a link, gets
// shipped through TestFlight/Firebase, and opens the freshly-installed
// app can have their referral code restored on first /api/public/config
// call (matched by IP hash). Row is deleted on successful signup-with-
// referral, or expires automatically after 24h.
export const pendingReferrals = pgTable("pending_referrals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ipHash: varchar("ip_hash", { length: 64 }).notNull(),
  referralCode: varchar("referral_code", { length: 32 }).notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  expiresAt: timestamp("expires_at").notNull(),
});

export type PendingReferral = typeof pendingReferrals.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Delivery decline reasons (per-driver soft-pass on an offer)
// ─────────────────────────────────────────────────────────────
export const declineReasonSchema = z.enum([
  "too_far",
  "not_enough_pay",
  "too_many_stops",
  "bad_timing",
  "not_interested",
  "other",
]);
export type DeclineReason = z.infer<typeof declineReasonSchema>;

export const deliveryDeclines = pgTable(
  "delivery_declines",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    deliveryRequestId: varchar("delivery_request_id")
      .notNull()
      .references(() => deliveryRequests.id, { onDelete: "cascade" }),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    reason: varchar("reason", { length: 30 }).notNull(),
    note: text("note"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    uniqueUserRequest: uniqueIndex("delivery_decline_unique_user_req").on(
      t.userId,
      t.deliveryRequestId,
    ),
    createdAtIdx: index("delivery_decline_created_at_idx").on(t.createdAt),
  }),
);

export const insertDeliveryDeclineSchema = createInsertSchema(deliveryDeclines).omit({
  id: true,
  createdAt: true,
});

export type InsertDeliveryDecline = z.infer<typeof insertDeliveryDeclineSchema>;
export type DeliveryDecline = typeof deliveryDeclines.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Gardens — AI gardening assistant
// ─────────────────────────────────────────────────────────────
export const gardenStatusSchema = z.enum(["setup", "planning", "active", "archived"]);
export type GardenStatus = z.infer<typeof gardenStatusSchema>;

export const gardenPlantStatusSchema = z.enum([
  "planned",
  "started",
  "planted",
  "harvesting",
  "done",
]);
export type GardenPlantStatus = z.infer<typeof gardenPlantStatusSchema>;

export const gardenStartMethodSchema = z.enum([
  "seed_indoor",
  "direct_sow",
  "buy_transplant",
]);
export type GardenStartMethod = z.infer<typeof gardenStartMethodSchema>;

export const gardenPhotoKindSchema = z.enum(["reference", "weekly", "diagnosis"]);
export type GardenPhotoKind = z.infer<typeof gardenPhotoKindSchema>;

export const gardenIssueCategorySchema = z.enum([
  "pest",
  "water",
  "fertilizer",
  "disease",
  "environmental",
  "unknown",
]);
export type GardenIssueCategory = z.infer<typeof gardenIssueCategorySchema>;

export const gardenReservedZoneKindSchema = z.enum([
  "pre_committed_plant",
  "blocked",
]);
export type GardenReservedZoneKind = z.infer<typeof gardenReservedZoneKindSchema>;

export const gardenSceneFeatureTypeSchema = z.enum([
  "bed",
  "fence",
  "path",
  "tree",
  "shed",
  "existing_plant",
  "lawn",
  "water",
  "obstacle",
  "door",
  "edge",
]);
export type GardenSceneFeatureType = z.infer<typeof gardenSceneFeatureTypeSchema>;

export const gardenSceneFeatureSchema = z.object({
  id: z.string(),
  type: gardenSceneFeatureTypeSchema,
  label: z.string(),
  worldX: z.number(),
  worldY: z.number(),
  worldWidth: z.number().nonnegative(),
  worldHeight: z.number().nonnegative(),
  rotationDeg: z.number(),
  confidence: z.enum(["high", "medium", "low"]),
});
export type GardenSceneFeature = z.infer<typeof gardenSceneFeatureSchema>;

export const gardenSceneGraphSchema = z.object({
  features: z.array(gardenSceneFeatureSchema),
  gardenExtentFt: z.object({
    width: z.number().positive(),
    height: z.number().positive(),
  }),
  anchor: z.enum(["sw", "center"]).default("sw"),
  notes: z.string().default(""),
});
export type GardenSceneGraph = z.infer<typeof gardenSceneGraphSchema>;

export const gardenReminderKindSchema = z.enum([
  "weekly_checkin",
  "seed_start",
  "transplant",
  "direct_sow",
  "harvest",
  "watering",
]);
export type GardenReminderKind = z.infer<typeof gardenReminderKindSchema>;

export const gardens = pgTable("gardens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  name: varchar("name", { length: 120 }),
  referencePhotoUrl: text("reference_photo_url"),
  referencePhotoWidth: integer("reference_photo_width"),
  referencePhotoHeight: integer("reference_photo_height"),
  referenceObjectLabel: varchar("reference_object_label", { length: 120 }),
  referenceLengthFt: decimal("reference_length_ft", { precision: 8, scale: 2 }),
  estimatedAreaSqFt: decimal("estimated_area_sq_ft", { precision: 10, scale: 2 }),
  scaleInchesPerPixel: decimal("scale_inches_per_pixel", { precision: 10, scale: 6 }),
  cardinalSouthDegrees: integer("cardinal_south_degrees"),
  hardinessZone: varchar("hardiness_zone", { length: 8 }),
  preferencesJson: jsonb("preferences_json"),
  planJson: jsonb("plan_json"),
  sceneGraph: jsonb("scene_graph").$type<GardenSceneGraph>(),
  status: varchar("status", { length: 20 }).default("setup").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertGardenSchema = createInsertSchema(gardens).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertGarden = z.infer<typeof insertGardenSchema>;
export type Garden = typeof gardens.$inferSelect;

export const gardenPlants = pgTable("garden_plants", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  gardenId: varchar("garden_id")
    .notNull()
    .references(() => gardens.id, { onDelete: "cascade" }),
  plantSlug: varchar("plant_slug", { length: 80 }).notNull(),
  variety: varchar("variety", { length: 120 }),
  quantity: integer("quantity").default(1).notNull(),
  startMethod: varchar("start_method", { length: 24 }).notNull(),
  seedStartDate: timestamp("seed_start_date"),
  transplantDate: timestamp("transplant_date"),
  plantedAt: timestamp("planted_at"),
  estimatedHarvestDate: timestamp("estimated_harvest_date"),
  gridX: integer("grid_x"),
  gridY: integer("grid_y"),
  status: varchar("status", { length: 20 }).default("planned").notNull(),
  notesJson: jsonb("notes_json"),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertGardenPlantSchema = createInsertSchema(gardenPlants).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertGardenPlant = z.infer<typeof insertGardenPlantSchema>;
export type GardenPlant = typeof gardenPlants.$inferSelect;

export const gardenPhotos = pgTable("garden_photos", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  gardenId: varchar("garden_id")
    .notNull()
    .references(() => gardens.id, { onDelete: "cascade" }),
  gardenPlantId: varchar("garden_plant_id").references(() => gardenPlants.id, {
    onDelete: "set null",
  }),
  photoUrl: text("photo_url").notNull(),
  kind: varchar("kind", { length: 20 }).notNull(),
  takenAt: timestamp("taken_at").defaultNow(),
  analysisJson: jsonb("analysis_json"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertGardenPhotoSchema = createInsertSchema(gardenPhotos).omit({
  id: true,
  createdAt: true,
  takenAt: true,
});
export type InsertGardenPhoto = z.infer<typeof insertGardenPhotoSchema>;
export type GardenPhoto = typeof gardenPhotos.$inferSelect;

export const gardenIssues = pgTable("garden_issues", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  gardenId: varchar("garden_id")
    .notNull()
    .references(() => gardens.id, { onDelete: "cascade" }),
  gardenPlantId: varchar("garden_plant_id").references(() => gardenPlants.id, {
    onDelete: "set null",
  }),
  photoUrl: text("photo_url").notNull(),
  userDescription: text("user_description"),
  diagnosisJson: jsonb("diagnosis_json"),
  status: varchar("status", { length: 20 }).default("open").notNull(),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").defaultNow(),
});

export const insertGardenIssueSchema = createInsertSchema(gardenIssues).omit({
  id: true,
  createdAt: true,
  resolvedAt: true,
});
export type InsertGardenIssue = z.infer<typeof insertGardenIssueSchema>;
export type GardenIssue = typeof gardenIssues.$inferSelect;

// Plant journal — identifications are user-scoped, not garden-scoped.
// A user can identify a sidewalk weed without owning a garden plot.
export const gardenIdentifications = pgTable("garden_identifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id")
    .notNull()
    .references(() => users.id, { onDelete: "cascade" }),
  photoUrl: text("photo_url").notNull(),
  commonName: text("common_name").notNull(),
  catalogSlug: varchar("catalog_slug", { length: 80 }),
  isWeed: boolean("is_weed").default(false).notNull(),
  identificationJson: jsonb("identification_json"),
  userLabel: text("user_label"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertGardenIdentificationSchema = createInsertSchema(gardenIdentifications).omit({
  id: true,
  createdAt: true,
});
export type InsertGardenIdentification = z.infer<typeof insertGardenIdentificationSchema>;
export type GardenIdentification = typeof gardenIdentifications.$inferSelect;

export const gardenIdentificationMessages = pgTable("garden_identification_messages", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  identificationId: varchar("identification_id")
    .notNull()
    .references(() => gardenIdentifications.id, { onDelete: "cascade" }),
  role: varchar("role", { length: 16 }).notNull(),
  content: text("content").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertGardenIdentificationMessageSchema = createInsertSchema(gardenIdentificationMessages).omit({
  id: true,
  createdAt: true,
});
export type InsertGardenIdentificationMessage = z.infer<typeof insertGardenIdentificationMessageSchema>;
export type GardenIdentificationMessage = typeof gardenIdentificationMessages.$inferSelect;

export const gardenIdentificationCheckins = pgTable("garden_identification_checkins", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  identificationId: varchar("identification_id")
    .notNull()
    .references(() => gardenIdentifications.id, { onDelete: "cascade" }),
  photoUrl: text("photo_url").notNull(),
  analysisJson: jsonb("analysis_json"),
  estimatedHarvestAt: timestamp("estimated_harvest_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertGardenIdentificationCheckinSchema = createInsertSchema(gardenIdentificationCheckins).omit({
  id: true,
  createdAt: true,
});
export type InsertGardenIdentificationCheckin = z.infer<typeof insertGardenIdentificationCheckinSchema>;
export type GardenIdentificationCheckin = typeof gardenIdentificationCheckins.$inferSelect;

// Single source of truth for a user's premium state: Stripe identity,
// subscription, and credit balances. PK on userId (one row per user).
// Created lazily on first checkout; users without rows are free-tier.
export const userBilling = pgTable("user_billing", {
  userId: varchar("user_id")
    .primaryKey()
    .references(() => users.id, { onDelete: "cascade" }),
  stripeCustomerId: varchar("stripe_customer_id", { length: 64 }),
  stripeSubscriptionId: varchar("stripe_subscription_id", { length: 64 }),
  subscriptionStatus: varchar("subscription_status", { length: 32 }),
  // Non-null only when the user holds the dark-launched delivery free-delivery
  // subscription ("neighborhood"). Set on subscription sync from the delivery
  // checkout's metadata.kind; null for Garden Premium-only or free-tier users.
  deliveryPlanTier: varchar("delivery_plan_tier", { length: 32 }),
  currentPeriodEnd: timestamp("current_period_end"),
  cancelAtPeriodEnd: boolean("cancel_at_period_end").default(false).notNull(),
  chatCredits: integer("chat_credits").default(0).notNull(),
  photoCredits: integer("photo_credits").default(0).notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export type UserBilling = typeof userBilling.$inferSelect;

export const gardenReservedZones = pgTable("garden_reserved_zones", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  gardenId: varchar("garden_id")
    .notNull()
    .references(() => gardens.id, { onDelete: "cascade" }),
  label: varchar("label", { length: 200 }).notNull(),
  kind: varchar("kind", { length: 32 }).notNull(),
  plantSlug: varchar("plant_slug", { length: 80 }),
  normX: decimal("norm_x", { precision: 6, scale: 5 }).notNull(),
  normY: decimal("norm_y", { precision: 6, scale: 5 }).notNull(),
  normWidth: decimal("norm_width", { precision: 6, scale: 5 }).notNull(),
  normHeight: decimal("norm_height", { precision: 6, scale: 5 }).notNull(),
  normRotation: decimal("norm_rotation", { precision: 6, scale: 2 }).default("0").notNull(),
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
});

export const insertGardenReservedZoneSchema = createInsertSchema(
  gardenReservedZones,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});
export type InsertGardenReservedZone = z.infer<typeof insertGardenReservedZoneSchema>;
export type GardenReservedZone = typeof gardenReservedZones.$inferSelect;

export const gardenReminders = pgTable(
  "garden_reminders",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    gardenId: varchar("garden_id")
      .notNull()
      .references(() => gardens.id, { onDelete: "cascade" }),
    gardenPlantId: varchar("garden_plant_id").references(() => gardenPlants.id, {
      onDelete: "cascade",
    }),
    kind: varchar("kind", { length: 32 }).notNull(),
    dueAt: timestamp("due_at").notNull(),
    firedAt: timestamp("fired_at"),
    payloadJson: jsonb("payload_json"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (t) => ({
    dueAtIdx: index("garden_reminders_due_at_idx").on(t.dueAt),
    gardenIdx: index("garden_reminders_garden_idx").on(t.gardenId),
  }),
);

export const insertGardenReminderSchema = createInsertSchema(gardenReminders).omit({
  id: true,
  createdAt: true,
  firedAt: true,
});
export type InsertGardenReminder = z.infer<typeof insertGardenReminderSchema>;
export type GardenReminder = typeof gardenReminders.$inferSelect;

export const gardensRelations = relations(gardens, ({ one, many }) => ({
  user: one(users, {
    fields: [gardens.userId],
    references: [users.id],
  }),
  plants: many(gardenPlants),
  photos: many(gardenPhotos),
  issues: many(gardenIssues),
  reservedZones: many(gardenReservedZones),
  reminders: many(gardenReminders),
}));

export const gardenPlantsRelations = relations(gardenPlants, ({ one, many }) => ({
  garden: one(gardens, {
    fields: [gardenPlants.gardenId],
    references: [gardens.id],
  }),
  reminders: many(gardenReminders),
}));

export const gardenPhotosRelations = relations(gardenPhotos, ({ one }) => ({
  garden: one(gardens, {
    fields: [gardenPhotos.gardenId],
    references: [gardens.id],
  }),
  plant: one(gardenPlants, {
    fields: [gardenPhotos.gardenPlantId],
    references: [gardenPlants.id],
  }),
}));

export const gardenIssuesRelations = relations(gardenIssues, ({ one }) => ({
  garden: one(gardens, {
    fields: [gardenIssues.gardenId],
    references: [gardens.id],
  }),
  plant: one(gardenPlants, {
    fields: [gardenIssues.gardenPlantId],
    references: [gardenPlants.id],
  }),
}));

export const gardenReservedZonesRelations = relations(
  gardenReservedZones,
  ({ one }) => ({
    garden: one(gardens, {
      fields: [gardenReservedZones.gardenId],
      references: [gardens.id],
    }),
  }),
);

export const gardenRemindersRelations = relations(gardenReminders, ({ one }) => ({
  garden: one(gardens, {
    fields: [gardenReminders.gardenId],
    references: [gardens.id],
  }),
  plant: one(gardenPlants, {
    fields: [gardenReminders.gardenPlantId],
    references: [gardenPlants.id],
  }),
}));

// ─────────────────────────────────────────────────────────────
// Reservations — pre-orders for items still on the plant
// ─────────────────────────────────────────────────────────────
// A reservation locks one or more units of a 'growing' item for the
// buyer. Buyer pays 25% (depositCents) at reservation time as a
// non-refundable commitment. The 75% balance is auto-charged off-
// session on estimatedRipeDate using the saved Stripe payment method.
//
// Status transitions:
//   held       — deposit captured, awaiting ripe date
//   captured   — balance charged successfully; downstream order created
//   capture_failed — off-session balance charge failed; buyer must
//                    confirm payment manually within 3 days or the
//                    reservation cancels and the seller keeps the deposit
//   cancelled_buyer — buyer cancelled before ripe; deposit forfeit
//   cancelled_seller — seller cancelled / crop failed; full refund
export const reservations = pgTable(
  "reservations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    buyerId: varchar("buyer_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    itemId: varchar("item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    sellerId: varchar("seller_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),

    quantity: integer("quantity").notNull(),
    unitPriceCents: integer("unit_price_cents").notNull(),
    depositCents: integer("deposit_cents").notNull(),
    balanceCents: integer("balance_cents").notNull(),
    // For meat shares, balanceCents is a processing-fee pass-through to the
    // seller's butcher rather than part of the item price. Snapshotted here so
    // settlement can forward it in full (0 for produce/made-to-order, where the
    // balance is part of the price and only the seller's net is transferred).
    processingFeeCents: integer("processing_fee_cents").default(0).notNull(),

    estimatedRipeDate: timestamp("estimated_ripe_date").notNull(),
    // Buyer's custom request for a made-to-order reservation (null for produce).
    requestText: text("request_text"),
    // Reference/inspiration photos the buyer attaches to a made-to-order
    // request (e.g. the cookie design they want). Reservations never become
    // orders, so there's no order chat to carry these — they live here.
    requestImageUrls: jsonb("request_image_urls").$type<string[]>(),
    // Fulfillment for the reservation. Null/"seller_pickup" = the legacy
    // behavior (coordinate pickup after capture). "community_delivery" means
    // the buyer chose free founding-maker delivery: on capture we spin up a
    // real order + founder delivery request from the fields below.
    fulfillmentMethod: varchar("fulfillment_method", { length: 50 }),
    deliveryAddressLine1: varchar("delivery_address_line_1", { length: 200 }),
    deliveryAddressLine2: varchar("delivery_address_line_2", { length: 200 }),
    deliveryCity: varchar("delivery_city", { length: 100 }),
    deliveryState: varchar("delivery_state", { length: 50 }),
    deliveryZipCode: varchar("delivery_zip_code", { length: 10 }),
    deliveryLat: decimal("delivery_lat", { precision: 10, scale: 6 }),
    deliveryLng: decimal("delivery_lng", { precision: 10, scale: 6 }),
    isFounderDelivery: boolean("is_founder_delivery").default(false).notNull(),
    // Set once the capture path creates the carrier order, so it's idempotent
    // and the reservation can link to its delivery.
    deliveryOrderId: varchar("delivery_order_id"),
    status: varchar("status", { length: 30 }).default("held").notNull(),

    stripeCustomerId: varchar("stripe_customer_id", { length: 64 }),
    stripePaymentMethodId: varchar("stripe_payment_method_id", { length: 64 }),
    depositPaymentIntentId: varchar("deposit_payment_intent_id", { length: 100 }),
    balancePaymentIntentId: varchar("balance_payment_intent_id", { length: 100 }),
    captureFailureReason: text("capture_failure_reason"),

    capturedAt: timestamp("captured_at"),
    cancelledAt: timestamp("cancelled_at"),
    notifiedRipeAt: timestamp("notified_ripe_at"),

    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at").defaultNow().notNull(),
  },
  (table) => [
    index("reservations_buyer_idx").on(table.buyerId, table.status),
    index("reservations_seller_idx").on(table.sellerId, table.status),
    index("reservations_ripe_date_idx").on(table.estimatedRipeDate, table.status),
  ],
);

export type Reservation = typeof reservations.$inferSelect;
export type InsertReservation = typeof reservations.$inferInsert;

// ─────────────────────────────────────────────────────────────
// Growth photos — weekly progress shots for 'growing' items
// ─────────────────────────────────────────────────────────────
// Sellers post one of these per week per growing item. Buyers viewing
// the listing or their reservation see the most recent photo and date
// so they can sanity-check the seller is actually tending the crop.
export const growthPhotos = pgTable(
  "growth_photos",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    itemId: varchar("item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    sellerId: varchar("seller_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    photoUrl: text("photo_url").notNull(),
    note: text("note"),
    estimatedRipeDate: timestamp("estimated_ripe_date"),
    takenAt: timestamp("taken_at").defaultNow().notNull(),
  },
  (table) => [
    index("growth_photos_item_taken_idx").on(table.itemId, table.takenAt),
  ],
);

export type GrowthPhoto = typeof growthPhotos.$inferSelect;
export type InsertGrowthPhoto = typeof growthPhotos.$inferInsert;

// ─────────────────────────────────────────────────────────────
// System settings (singleton key/value, admin-controlled)
// ─────────────────────────────────────────────────────────────
export const systemSettings = pgTable("system_settings", {
  key: varchar("key", { length: 80 }).primaryKey(),
  value: text("value").notNull(),
  updatedAt: timestamp("updated_at").defaultNow(),
  updatedByUserId: varchar("updated_by_user_id").references(() => users.id, {
    onDelete: "set null",
  }),
});

export type SystemSetting = typeof systemSettings.$inferSelect;

// ─────────────────────────────────────────────────────────────
// Founder Buyer grants (Founding program, Phase 2)
// ─────────────────────────────────────────────────────────────
// One row per buyer who signed up via a Founding Maker's referral code and
// became one of that seller's founder buyers. sellerId is denormalized from
// users.referredBy at grant time so the per-seller tally survives a later
// referredBy scrub (e.g. account deletion PII clean-up). Per-seller count is
// COUNT(*) WHERE seller_id = ?; the per-seller cap (default 10) is enforced
// in code, not by a DB constraint. freeDeliveryOrdersUsed is incremented only
// on payment_intent.succeeded; the cap (2) is also enforced in code.
export const founderBuyerGrants = pgTable(
  "founder_buyer_grants",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    buyerId: varchar("buyer_id")
      .notNull()
      .unique()
      .references(() => users.id, { onDelete: "cascade" }),
    sellerId: varchar("seller_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    freeDeliveryOrdersUsed: integer("free_delivery_orders_used")
      .default(0)
      .notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    sellerIdx: index("founder_buyer_grants_seller_idx").on(table.sellerId),
  }),
);

export type FounderBuyerGrant = typeof founderBuyerGrants.$inferSelect;
export type InsertFounderBuyerGrant = typeof founderBuyerGrants.$inferInsert;

// Demand signal for the dark Neighborhood delivery subscription: a buyer in a
// density-short area asks to be notified when free-delivery plans unlock. One
// open row per buyer (upsert by buyerId). Lets Mark see which zips have waiting
// buyers but too few sellers, to target seller recruitment.
export const deliverySubscriptionInterest = pgTable(
  "delivery_subscription_interest",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    buyerId: varchar("buyer_id")
      .notNull()
      .unique()
      .references(() => users.id, { onDelete: "cascade" }),
    zipCode: varchar("zip_code", { length: 10 }),
    latitude: decimal("latitude", { precision: 10, scale: 6 }),
    longitude: decimal("longitude", { precision: 10, scale: 6 }),
    sellerCountAtSignup: integer("seller_count_at_signup").default(0).notNull(),
    createdAt: timestamp("created_at").defaultNow(),
  },
);

export type DeliverySubscriptionInterest = typeof deliverySubscriptionInterest.$inferSelect;
export type InsertDeliverySubscriptionInterest = typeof deliverySubscriptionInterest.$inferInsert;

// ─────────────────────────────────────────────────────────────
// Beta tester mission progress
// ─────────────────────────────────────────────────────────────
export const missionProgress = pgTable(
  "mission_progress",
  {
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    missionId: varchar("mission_id", { length: 50 }).notNull(),
    status: varchar("status", { length: 20 }).default("available").notNull(),
    attempts: integer("attempts").default(0).notNull(),
    pointsAwarded: integer("points_awarded").default(0).notNull(),
    completedAt: timestamp("completed_at"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => [
    primaryKey({ columns: [table.userId, table.missionId] }),
    index("mission_progress_user_idx").on(table.userId),
  ],
);

export type MissionProgress = typeof missionProgress.$inferSelect;
export type InsertMissionProgress = typeof missionProgress.$inferInsert;

// ─────────────────────────────────────────────────────────────
// Listing notify-me signups
// ─────────────────────────────────────────────────────────────
// When a buyer taps "Notify me when available" on a listing whose seller
// hasn't completed Stripe onboarding (status = 'published_pending_payments'),
// we record the signup here. The account.updated webhook checks this table
// when a seller's chargesEnabled flips true and fires push notifications to
// every buyer waiting on that seller's listings.
//
// Reused for the broader pre-launch reservation system (early-access spec):
// pre-launch reservations are conceptually the same — buyer marks intent,
// gets notified when checkout becomes available. One table, two trigger
// sources (per-seller verification, platform-wide launch).
export const notifySignups = pgTable(
  "notify_signups",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    itemId: varchar("item_id")
      .notNull()
      .references(() => items.id, { onDelete: "cascade" }),
    sellerId: varchar("seller_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    // 'pending' = still waiting for trigger
    // 'fired' = notification was sent
    // 'cancelled' = buyer removed it before trigger
    status: varchar("status", { length: 20 }).default("pending").notNull(),
    createdAt: timestamp("created_at").defaultNow(),
    firedAt: timestamp("fired_at"),
  },
  (table) => [
    index("notify_signups_user_idx").on(table.userId),
    index("notify_signups_seller_status_idx").on(table.sellerId, table.status),
    index("notify_signups_item_idx").on(table.itemId),
  ],
);

export type NotifySignup = typeof notifySignups.$inferSelect;
export type InsertNotifySignup = typeof notifySignups.$inferInsert;

// ─────────────────────────────────────────────────────────────
// Seller Compliance Attestations
// ─────────────────────────────────────────────────────────────
// Required before a seller can publish their first listing. Sellers attest
// that they've reviewed state-specific food/sale laws (cottage food rules,
// sales tax registration, etc.) and accept responsibility for compliance.
// Stoop is not authoritative on the laws — we provide curated links to
// official sources and an audit trail of what each seller acknowledged.
//
// `attestationVersion` lets us bump the policy text and force re-attest
// without losing prior audit history. Each attest creates a new row;
// `getLatestAttestation(userId)` reads the most recent.
export const sellerComplianceCategorySchema = z.enum([
  "produce",
  "baked_goods",
  "prepared_foods",
  "dairy_eggs",
  "meat_poultry",
  "non_food",
]);

export const sellerComplianceAttestations = pgTable(
  "seller_compliance_attestations",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    state: varchar("state", { length: 50 }).notNull(),
    // Comma-separated list of sellerComplianceCategorySchema values; ordered
    // by user selection. Stored as text instead of pg array to keep the
    // schema simple — this column is read whole, never queried into.
    categoriesCsv: text("categories_csv").notNull(),
    attestationVersion: varchar("attestation_version", { length: 20 }).notNull(),
    // Each acknowledgment is its own column rather than a JSON blob so
    // future audits can SELECT specific acknowledgments cleanly.
    ackReadSources: integer("ack_read_sources").notNull(),
    ackComplianceLaws: integer("ack_compliance_laws").notNull(),
    ackPermitsObtained: integer("ack_permits_obtained").notNull(),
    ackStoopNotLiable: integer("ack_stoop_not_liable").notNull(),
    ipAddress: varchar("ip_address", { length: 64 }),
    userAgent: text("user_agent"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("seller_compliance_user_created_idx").on(table.userId, table.createdAt),
  ],
);

export type SellerComplianceAttestation = typeof sellerComplianceAttestations.$inferSelect;
export type InsertSellerComplianceAttestation = typeof sellerComplianceAttestations.$inferInsert;

// Client-fired beacon table. Filled by useAnalytics on app_open and on
// every route change; aggregated on the admin dashboard for real DAU /
// MAU / page-view metrics. Anonymous (no user_id) events are allowed
// for pre-login pageviews. Path stored for top-pages aggregation.
export const analyticsEvents = pgTable(
  "analytics_events",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    userId: varchar("user_id"),
    sessionId: varchar("session_id", { length: 64 }).notNull(),
    kind: varchar("kind", { length: 30 }).notNull(),
    path: varchar("path", { length: 240 }),
    platform: varchar("platform", { length: 16 }),
    // Event-specific detail: scroll depth %, dwell ms, error message/stack,
    // funnel reason, etc. Kept small (capped server-side).
    metadata: jsonb("metadata"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("analytics_events_created_idx").on(table.createdAt),
    index("analytics_events_user_created_idx").on(table.userId, table.createdAt),
    index("analytics_events_kind_created_idx").on(table.kind, table.createdAt),
  ],
);

export type AnalyticsEvent = typeof analyticsEvents.$inferSelect;
export type InsertAnalyticsEvent = typeof analyticsEvents.$inferInsert;

// Every settled browse search, with what it returned and what the searcher then
// picked. Separate from analytics_events on purpose: this is a durable
// relevance dataset (review a query against its results to judge accuracy,
// build a per-user taste profile, evaluate future ranking) rather than a
// funnel firehose, so it wants its own shape and its own indexes.
export const searchQueries = pgTable(
  "search_queries",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // Null for guests — they search too, and their queries are still demand.
    userId: varchar("user_id"),
    sessionId: varchar("session_id", { length: 64 }).notNull(),
    query: varchar("query", { length: 200 }).notNull(),
    // Per-surface result counts, so "found nothing" is distinguishable from
    // "found no products but did find a maker".
    itemCount: integer("item_count").default(0).notNull(),
    producerCount: integer("producer_count").default(0).notNull(),
    boxCount: integer("box_count").default(0).notNull(),
    // Whether the AI assist layer ran, and what vocabulary it returned.
    assistUsed: integer("assist_used").default(0).notNull(),
    assistTerms: jsonb("assist_terms"),
    // What the searcher opened from these results. The accuracy signal: a query
    // whose results were never clicked is a query we answered badly.
    clickedItemId: varchar("clicked_item_id"),
    clickedProducerId: varchar("clicked_producer_id"),
    clickedAt: timestamp("clicked_at"),
    platform: varchar("platform", { length: 16 }),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("search_queries_created_idx").on(table.createdAt),
    index("search_queries_user_created_idx").on(table.userId, table.createdAt),
    index("search_queries_query_idx").on(table.query),
  ],
);

export type SearchQuery = typeof searchQueries.$inferSelect;
export type InsertSearchQuery = typeof searchQueries.$inferInsert;

// Demand for something NOBODY on Stoop sells. Distinct from items.status
// 'on_request', which is a maker's own listing toggled off — this is a buyer
// telling us about a gap in the marketplace. It is the recruitment signal:
// what to show prospective sellers, and where.
export const itemDemand = pgTable(
  "item_demand",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // Null for guests. A guest request is still a real signal, it just can't
    // be notified when the item shows up.
    userId: varchar("user_id"),
    sessionId: varchar("session_id", { length: 64 }),
    // What they searched for, plus anything they added about what they meant.
    query: varchar("query", { length: 200 }).notNull(),
    note: varchar("note", { length: 500 }),
    // Where the demand is — the whole point for maker recruitment.
    zipCode: varchar("zip_code", { length: 12 }),
    city: varchar("city", { length: 120 }),
    status: varchar("status", { length: 20 }).default("open").notNull(),
    // Set when a newly published listing matched this request.
    matchedItemId: varchar("matched_item_id"),
    notifiedAt: timestamp("notified_at"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("item_demand_status_created_idx").on(table.status, table.createdAt),
    index("item_demand_user_idx").on(table.userId),
  ],
);

export type ItemDemand = typeof itemDemand.$inferSelect;
export type InsertItemDemand = typeof itemDemand.$inferInsert;

// "I know someone who makes this." A buyer naming a neighbor is the warmest
// recruitment lead there is, so it gets captured as a first-class pipeline
// rather than only firing a share sheet.
export const makerLeads = pgTable(
  "maker_leads",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    // Who suggested them (null for guests).
    userId: varchar("user_id"),
    sessionId: varchar("session_id", { length: 64 }),
    // The request that prompted the suggestion, when there was one.
    demandId: varchar("demand_id"),
    makerName: varchar("maker_name", { length: 160 }).notNull(),
    // Free text on purpose: a phone, an email, an Instagram handle, or "the
    // guy at the Waterford farm stand" are all useful leads.
    makerContact: varchar("maker_contact", { length: 200 }),
    note: varchar("note", { length: 500 }),
    status: varchar("status", { length: 20 }).default("new").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("maker_leads_status_created_idx").on(table.status, table.createdAt),
  ],
);

export type MakerLead = typeof makerLeads.$inferSelect;
export type InsertMakerLead = typeof makerLeads.$inferInsert;

// Triage chat thread. When the agent needs human input it posts a message
// (author_role 'agent') instead of guessing; the admin (or the original
// reporter) replies, which re-queues a triage run with the thread injected.
export const feedbackMessages = pgTable(
  "feedback_messages",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    feedbackId: varchar("feedback_id")
      .notNull()
      .references(() => feedback.id, { onDelete: "cascade" }),
    authorRole: varchar("author_role", { length: 16 }).notNull(),
    authorUserId: varchar("author_user_id"),
    body: text("body").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("feedback_messages_feedback_created_idx").on(table.feedbackId, table.createdAt),
  ],
);

export type FeedbackMessage = typeof feedbackMessages.$inferSelect;
export type InsertFeedbackMessage = typeof feedbackMessages.$inferInsert;

// Seller expense ledger — sellers log what they spend growing/making
// what they sell (seeds, soil, supplies) so they can export a CSV at
// tax time and write the costs off against their Stoop sales income.
export const sellerExpenses = pgTable(
  "seller_expenses",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    sellerId: varchar("seller_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    expenseDate: varchar("expense_date", { length: 10 }).notNull(),
    category: varchar("category", { length: 40 }).default("other").notNull(),
    vendor: varchar("vendor", { length: 200 }),
    amount: decimal("amount", { precision: 10, scale: 2 }).notNull(),
    miles: decimal("miles", { precision: 10, scale: 2 }),
    notes: text("notes"),
    receiptImageUrl: text("receipt_image_url"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
  },
  (table) => [
    index("seller_expenses_seller_date_idx").on(table.sellerId, table.expenseDate),
  ],
);

export const insertSellerExpenseSchema = createInsertSchema(sellerExpenses).omit({
  id: true,
  sellerId: true,
  createdAt: true,
  receiptImageUrl: true,
});

export type SellerExpense = typeof sellerExpenses.$inferSelect;
export type InsertSellerExpense = z.infer<typeof insertSellerExpenseSchema>;

// ─────────────────────────────────────────────────────────────
// Admin <-> user direct support messages. Distinct from order chat
// (which is buyer<->seller, scoped to an order) and from feedback
// threads (scoped to a feedback report). This is a plain DM thread
// between the Stoop admin and any single user. Authorization is
// enforced at the route layer: the admin may message anyone; a
// non-admin may only message the admin.
// ─────────────────────────────────────────────────────────────
export const supportMessages = pgTable(
  "support_messages",
  {
    id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
    senderId: varchar("sender_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    recipientId: varchar("recipient_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    body: text("body").notNull(),
    // Optional tappable quick-reply options the admin attaches to a message
    // (a lightweight poll). The recipient taps one and it sends as their reply.
    quickReplies: jsonb("quick_replies").$type<string[]>(),
    isRead: integer("is_read").default(0).notNull(),
    // For admin-sent messages: the identity the admin chose to "send as"
    // ("Stoop Support" vs "Mark from Stoop"), so the recipient's in-app thread
    // label and notification reflect that choice instead of a hardcoded default.
    fromTitle: varchar("from_title", { length: 80 }),
    reactions: jsonb("reactions").$type<Record<string, string>>(),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => [
    index("support_messages_pair_created_idx").on(
      table.senderId,
      table.recipientId,
      table.createdAt,
    ),
    index("support_messages_recipient_read_idx").on(table.recipientId, table.isRead),
  ],
);

export type SupportMessage = typeof supportMessages.$inferSelect;
export type InsertSupportMessage = typeof supportMessages.$inferInsert;
